// ./src/utils/api/EventArtistService.ts
//
// EventArtist (a booked act / lineup slot on an event) service. OpResult-native,
// event-scoped. A booking references a catalog Artist (name / photo / deal are
// snapshotted server-side on create) OR carries a typed name for an ad-hoc act,
// plus its lineup billing, booking status + milestones, money terms, party size
// and document attachments. Mirrors EventPartnerService — swap the entity. See
// em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { listAllPages, ListAllOptions } from "./listAllPages";
import { Pagination, SortItem } from "./commonApiTypes";
// Reuse the shared resolved-file shape (exported by EventPartnerService) to avoid a
// duplicate export of the same name through the api barrel.
import type { ResolvedFileRef } from "./EventPartnerService";
// The booking fee mirrors into an event budget item; reuse its type so the linked
// item can be passed straight to BudgetPaymentsDrawer.
import type { BudgetItem } from "./BudgetItemService";

// =============================================================================
// Enum-like constants (mirror the eventsmagic-api boundary/eventArtist.ts)
// =============================================================================

export const ARTIST_BILLING = {
  HEADLINER: "headliner",
  CO_HEADLINE: "co_headline",
  SUPPORT: "support",
  OPENER: "opener",
  GUEST: "guest",
} as const;

export type ArtistBilling = (typeof ARTIST_BILLING)[keyof typeof ARTIST_BILLING];

export const ARTIST_BILLING_VALUES = Object.values(ARTIST_BILLING);

export const ARTIST_BILLING_LABELS: Record<string, string> = {
  headliner: "Headliner",
  co_headline: "Co-headline",
  support: "Support",
  opener: "Opener",
  guest: "Guest",
};

export const ARTIST_BOOKING_STATUS = {
  INQUIRY: "inquiry",
  HELD: "held",
  OFFER_SENT: "offer_sent",
  CONFIRMED: "confirmed",
  COMPLETED: "completed",
  SETTLED: "settled",
  CANCELLED: "cancelled",
  RELEASED: "released",
} as const;

export type ArtistBookingStatus = (typeof ARTIST_BOOKING_STATUS)[keyof typeof ARTIST_BOOKING_STATUS];

export const ARTIST_BOOKING_STATUS_VALUES = Object.values(ARTIST_BOOKING_STATUS);

export const ARTIST_BOOKING_STATUS_LABELS: Record<string, string> = {
  inquiry: "Inquiry",
  held: "Held",
  offer_sent: "Offer sent",
  confirmed: "Confirmed",
  completed: "Completed",
  settled: "Settled",
  cancelled: "Cancelled",
  released: "Released",
};

/** Color hint for a booking-status badge (mirrors the list/badge treatment). */
export const ARTIST_BOOKING_STATUS_COLORS: Record<string, string> = {
  inquiry: "gray",
  held: "yellow",
  offer_sent: "indigo",
  confirmed: "green",
  completed: "teal",
  settled: "blue",
  cancelled: "red",
  released: "orange",
};

export const EVENT_ARTIST_DEAL_TYPE = {
  NONE: "none",
  FLAT_GUARANTEE: "flat_guarantee",
  DOOR_SPLIT: "door_split",
  GUARANTEE_VS_PERCENTAGE: "guarantee_vs_percentage",
} as const;

export type EventArtistDealType = (typeof EVENT_ARTIST_DEAL_TYPE)[keyof typeof EVENT_ARTIST_DEAL_TYPE];

export const EVENT_ARTIST_DEAL_TYPE_VALUES = Object.values(EVENT_ARTIST_DEAL_TYPE);

export const EVENT_ARTIST_DEAL_TYPE_LABELS: Record<string, string> = {
  none: "None",
  flat_guarantee: "Flat guarantee",
  door_split: "Door split",
  guarantee_vs_percentage: "Guarantee vs. percentage",
};

/** Deal types where a percentage (of the door / gross) applies. */
export const ARTIST_DEAL_TYPES_WITH_PERCENTAGE: string[] = [
  EVENT_ARTIST_DEAL_TYPE.DOOR_SPLIT,
  EVENT_ARTIST_DEAL_TYPE.GUARANTEE_VS_PERCENTAGE,
];

export const ARTIST_FILE_KIND = {
  CONTRACT: "contract",
  RIDER: "rider",
  STAGE_PLOT: "stage_plot",
  TECH_SPEC: "tech_spec",
  INVOICE: "invoice",
  OTHER: "other",
} as const;

export type ArtistFileKind = (typeof ARTIST_FILE_KIND)[keyof typeof ARTIST_FILE_KIND];

export const ARTIST_FILE_KIND_VALUES = Object.values(ARTIST_FILE_KIND);

export const ARTIST_FILE_KIND_LABELS: Record<string, string> = {
  contract: "Contract",
  rider: "Rider",
  stage_plot: "Stage plot",
  tech_spec: "Tech spec",
  invoice: "Invoice",
  other: "Other",
};

export const billingLabel = (v?: string | null): string => (v ? ARTIST_BILLING_LABELS[v] ?? v : "");
export const bookingStatusLabel = (v?: string | null): string => (v ? ARTIST_BOOKING_STATUS_LABELS[v] ?? v : "");
export const bookingStatusColor = (v?: string | null): string => (v ? ARTIST_BOOKING_STATUS_COLORS[v] ?? "gray" : "gray");

// =============================================================================
// GraphQL
// =============================================================================

const EVENT_ARTIST_FILE_FIELDS = `
  id
  fileKind
  uploadedFileId
  uploadedFile { id fileUrl thumbnailUrl }
  fileName
  contentType
  fileSize
  orderNo
`;

export const EVENT_ARTIST_FIELDS = `
  id
  accountId
  eventId
  artistId
  name
  mainImageUploadedFileId
  mainImageUploadedFile { id fileUrl thumbnailUrl }
  billing
  billingIndex
  bookingStatus
  holdRank
  holdExpiresAt
  contractSigned
  depositPaid
  advanceComplete
  dealType
  amount
  percentage
  depositAmount
  dealDoorAmount
  dealPayoutAmount
  currencyCode
  trackInBudget
  budgetItemId
  budgetItem {
    id
    name
    expectedAmount
    paidAmount
    remainingAmount
    currencyCode
    calcType
    unitAmount
    eventId
    accountId
  }
  blurb
  entourageCount
  notes
  settings
  files { ${EVENT_ARTIST_FILE_FIELDS} }
  performances { id eventTimeSlotId setStartsAt setEndsAt mapElementId }
  orderNo
  status
  rights
  createdAt
  updatedAt
`;

const EVENT_ARTIST_LIST_QUERY = gql`
  query EventArtistList($filter: EventArtistFilter, $params: PaginationAndSorting) {
    eventArtistList(filter: $filter, params: $params) {
      code
      total
      data { ${EVENT_ARTIST_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const EVENT_ARTIST_GET_QUERY = gql`
  query EventArtistGet($id: ID) {
    eventArtistGet(id: $id) {
      code
      data { ${EVENT_ARTIST_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const EVENT_ARTIST_GET_MANY_QUERY = gql`
  query EventArtistGetMany($ids: [ID]) {
    eventArtistGetMany(ids: $ids) {
      code
      data { ${EVENT_ARTIST_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const EVENT_ARTIST_CREATE_MUTATION = gql`
  mutation EventArtistCreate($params: EventArtistInput) {
    eventArtistCreate(params: $params) {
      code
      data { ${EVENT_ARTIST_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const EVENT_ARTIST_UPDATE_MUTATION = gql`
  mutation EventArtistUpdate($where: EventArtistWhereInput, $params: EventArtistInput) {
    eventArtistUpdate(where: $where, params: $params) {
      code
      data { ${EVENT_ARTIST_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

// #165 round 2 — copy one date's lineup to other dates (each act becomes a new booking on each date).
const EVENT_ARTIST_COPY_TO_DATES_MUTATION = gql`
  mutation EventArtistCopyToDates($params: EventArtistCopyToDatesInput) {
    eventArtistCopyToDates(params: $params) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

const EVENT_ARTIST_REMOVE_MUTATION = gql`
  mutation EventArtistRemove($where: EventArtistWhereInput) {
    eventArtistRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types
// =============================================================================

/** One document attachment on a booking (contract, rider, stage plot…). */
export interface EventArtistFile {
  id?: string;
  fileKind?: string | null;
  uploadedFileId?: string | null;
  uploadedFile?: ResolvedFileRef | null;
  fileName?: string | null;
  contentType?: string | null;
  fileSize?: number | null;
  orderNo?: number | null;
}

export interface EventArtist {
  id?: string;
  accountId?: string | null;
  eventId?: string | null;
  /** Soft link → the account's catalog Artist. Null for an ad-hoc, typed act. */
  artistId?: string | null;
  /** Display / lineup name — snapshotted from the artist, or typed for an ad-hoc act. */
  name?: string | null;
  /** Photo override; falls back to the catalog artist's image server-side. */
  mainImageUploadedFileId?: string | null;
  mainImageUploadedFile?: ResolvedFileRef | null;
  billing?: string | null;
  billingIndex?: number | null;
  bookingStatus?: string | null;
  /** Priority when the act is only HELD (1 = first refusal). */
  holdRank?: number | null;
  holdExpiresAt?: string | null;
  contractSigned?: boolean | null;
  depositPaid?: boolean | null;
  advanceComplete?: boolean | null;
  dealType?: string | null;
  amount?: number | null;
  percentage?: number | null;
  depositAmount?: number | null;
  /** #235 — a door deal only: the door so far for the act's dates (admissions, net of refunds, tax and guest-paid fees). */
  dealDoorAmount?: number | null;
  /** #235 — what the deal pays now (flat: its amount; a door deal: on the door so far). The same as its budget line. */
  dealPayoutAmount?: number | null;
  currencyCode?: string | null;
  /** When true, the fee mirrors into an event budget item and payments live there. */
  trackInBudget?: boolean;
  /** Read-only: the linked event budget item's id (set by the server when tracked). */
  budgetItemId?: string | null;
  /** Read-only: the live-resolved linked budget item (derived paid / remaining live here). */
  budgetItem?: BudgetItem | null;
  /** Public lineup text shown on the event website. */
  blurb?: string | null;
  entourageCount?: number | null;
  notes?: string | null;
  settings?: Record<string, unknown> | null;
  files?: EventArtistFile[] | null;
  /** #165 round 2 — the dates / sessions it plays, each with the act's own set (time + stage) there. */
  performances?: EventArtistPerformance[] | null;
  orderNo?: number | null;
  status?: number;
  rights?: string[] | null;
  createdAt?: string;
  updatedAt?: string;
}

/** #165 round 2 — one date / session a booking plays, with its set: null times = the date's own; null stage = the date's. */
export interface EventArtistPerformance {
  id?: string | null;
  eventTimeSlotId: string;
  setStartsAt?: string | null;
  setEndsAt?: string | null;
  mapElementId?: string | null;
}

/** The FULL desired attachment set, diffed on id server-side; omit to leave untouched. */
export interface EventArtistFileInput {
  id?: string;
  fileKind?: string | null;
  uploadedFileId?: string | null;
  fileName?: string | null;
  contentType?: string | null;
  fileSize?: number | null;
}

export interface EventArtistInput {
  /** Required on create; omitted on update. */
  eventId?: string | null;
  artistId?: string | null;
  name?: string | null;
  mainImageUploadedFileId?: string | null;
  billing?: string | null;
  billingIndex?: number | null;
  bookingStatus?: string | null;
  holdRank?: number | null;
  holdExpiresAt?: string | null;
  contractSigned?: boolean | null;
  depositPaid?: boolean | null;
  advanceComplete?: boolean | null;
  dealType?: string | null;
  amount?: number | null;
  percentage?: number | null;
  depositAmount?: number | null;
  trackInBudget?: boolean;
  blurb?: string | null;
  entourageCount?: number | null;
  notes?: string | null;
  settings?: Record<string, unknown> | null;
  /** Full desired set, diffed on id server-side; omit to leave untouched. */
  files?: EventArtistFileInput[] | null;
  /** #165 round 2 — the FULL set of dates it plays (each once); omit to leave them untouched. */
  performances?: Array<Omit<EventArtistPerformance, 'id'>> | null;
  status?: number | null;
  orderNo?: number | null;
}

// A bulk update (>1 id) may only mass-change status (server-enforced).
export interface EventArtistBulkInput {
  status?: number | null;
}

export interface EventArtistFilter {
  id?: string[] | null;
  eventId?: string[] | null;
  artistId?: string[] | null;
  bookingStatus?: string[] | null;
  billing?: string[] | null;
  status?: number[] | null;
  searchKeyword?: string | null;
  /** #165 round 2 — only the acts playing these dates / sessions. */
  eventTimeSlotId?: string[] | null;
}

export interface EventArtistWhereInput {
  id?: string | string[];
}

/** Display name for a booking. */
export const eventArtistDisplayName = (a: EventArtist): string => {
  if (a.name && a.name.trim()) {
    return a.name.trim();
  }

  return "—";
};

/** Showcase photo: the booking's own override, else the (server-resolved) catalog artist image. */
export const eventArtistPhotoUrl = (a: EventArtist): string | null =>
  a.mainImageUploadedFile?.fileUrl || a.mainImageUploadedFile?.thumbnailUrl || null;

// =============================================================================
// Service
// =============================================================================

export class EventArtistService {
  static async list(filter?: EventArtistFilter, pagination?: Pagination, sortBy?: SortItem[]): Promise<OpResult> {
    return ApiService.executeQuery(EVENT_ARTIST_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  /**
   * Fetches EVERY booking matching `filter` by paging through the list — for whole-set
   * consumers (export) that must cover the full filtered set, not one page.
   */
  static async listAll(
    filter?: EventArtistFilter,
    sortBy?: SortItem[],
    opts?: ListAllOptions
  ): Promise<OpResult> {
    return listAllPages<EventArtist>(EventArtistService.list, filter, sortBy, opts);
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(EVENT_ARTIST_GET_QUERY, { id });
  }

  static async getMany(ids: string[]): Promise<OpResult> {
    return ApiService.executeQuery(EVENT_ARTIST_GET_MANY_QUERY, { ids });
  }

  static async create(params: EventArtistInput): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_ARTIST_CREATE_MUTATION, { params });
  }

  static async update(where: EventArtistWhereInput, params: EventArtistInput): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_ARTIST_UPDATE_MUTATION, { where, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_ARTIST_REMOVE_MUTATION, { where: { id } });
  }

  static async bulkUpdate(ids: string[], params: EventArtistBulkInput): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(EVENT_ARTIST_UPDATE_MUTATION, { where: { id: ids }, params });
  }

  /** #165 round 2 — copy one date's lineup to other dates; `data` is the bookings it created (an act already there is skipped). */
  static async copyToDates(params: { fromEventTimeSlotId: string; toEventTimeSlotIds: string[]; eventArtistIds?: string[] | null }): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_ARTIST_COPY_TO_DATES_MUTATION, { params });
  }

  static async removeMany(ids: string[]): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(EVENT_ARTIST_REMOVE_MUTATION, { where: { id: ids } });
  }
}
